import { getRestBaseUrl, RestClientOptions, RestClientV5 } from "libs/bybit-api/src";
import { MarketError } from "../error/codes";
import { firstValueFrom, tap, defer, retry, catchError, throwError, switchMap, of } from "rxjs";
import { MarketLoggerService, RestRequestLogRow } from "./market-logger.service";
import { isEmpty } from "lodash-es";
import { AxiosRequestConfig } from "axios";
import { BybitTimeService } from "./market-time.service";

export class BybitRestClient extends RestClientV5 {

  static timeService?: BybitTimeService

  constructor(
    restOptions: RestClientOptions = {},
    networkOptions: AxiosRequestConfig = {},
    private marketLogger: MarketLoggerService,
  ) {
    super(restOptions, networkOptions)
  }

  /**
   * Заменяем метод синхронизации времени
   */
  override syncTime(): Promise<any> {
    if (BybitRestClient.timeService) {
      return firstValueFrom(BybitRestClient.timeService.offset$.pipe(
        tap(offset => this.setTimeOffset(offset))
      ))
    }
    throw new Error('Time offset observable not initialized')
  }

  /**
   * Логирует ответ от биржи в market log
   */
  private logResponse(url: string, params: any, response: any) {
    const {url: parsedUrl, params: queryParams} = this.parseUrl(url)
    params = typeof(params) == 'object' ? params : {}
    const data = {...queryParams, ...params}
    const logRow: RestRequestLogRow = {
      time: Date.now(),
      market: 'bybit',
      type: 'rest',
      url: parsedUrl,
      params: isEmpty(data) ? undefined : data,
      httpCode: 200,
      apiCode: response.retCode,
      apiMessage: response.retMsg,
      apiDetails: isEmpty(response.retExtInfo) ? undefined : response.retExtInfo,
    }
    if (this.logFilter(logRow)) {
      this.marketLogger.log(logRow)
    }
  }

  /**
   * Проверяет ответ на наличие ошибки
   */
  private checkResponseError(response: any) {
    return response.retCode !== 0
  }

  /**
   * Логирует ошибку от биржи в weblog
   */
  private logError(e: any) {
    if (e && this.logFilter(e)) {
      // Это ошибка от биржи сконвертированная либой
      if ('requestUrl' in e && 'code' in e) {
        const {url: parsedUrl, params: queryParams} = this.parseUrl(e.requestUrl)
        const bodyParams = typeof(e.requestBody) == 'object' ? e.requestBody : {}
        const data = {...queryParams, ...bodyParams}
        const logRow: RestRequestLogRow = {
          time: Date.now(),
          market: 'bybit',
          type: 'rest',
          url: parsedUrl,
          params: isEmpty(data) ? undefined : data,
          httpCode: e.code || 0,
          apiCode: e.code || 0,
          apiMessage: e.message || ''
        }
        this.marketLogger.log(logRow)
      }
    }
  }

  /**
   * Конвертирует ошибку биржи в MarketError
   */
  private convertError(e: any): any {
    if ('retCode' in e && 'retMsg' in e) {
      return new MarketError(e.retCode.toString(), e.retMsg, e.retExtInfo)
    }
    return e
  }

  /**
   * Фильтрует ошибку для логирования в weblog
   */
  private logFilter(e: any) {
    if ((e instanceof MarketError || 'apiCode' in e) && e.apiCode == '10010') {  // Unmatched IP
      return false
    }
    return true
  }

  private parseUrl(url: string): {url: string, params: Record<string, any> | undefined} {
    try {
      const objUrl = new URL(url)
      const queryParams = Object.fromEntries(objUrl.searchParams.entries())
      return {url: objUrl.origin + objUrl.pathname, params: queryParams}
    } catch {
      const baseUrl = getRestBaseUrl(false, {})
      return {url: `${baseUrl}${url}`, params: undefined}
    }
  }

  private executeRequest(endpoint: string, params: any, callback: () => Promise<any>): Promise<any> {
    return firstValueFrom(
      defer(() => callback()).pipe(
        switchMap(response => {
          if (this.checkResponseError(response)) {
            const serverTime = response.time as number
            const convertedError = this.convertError(response)
            if (convertedError instanceof MarketError && convertedError.apiCode === '10002' && BybitRestClient.timeService) {
              BybitRestClient.timeService.setServerTime(serverTime)
              this.setTimeOffset(serverTime - Date.now())
            }
            return throwError(() => convertedError)
          }
          return of(response)
        }),
        retry({count: 2, delay: 1000}),
        tap(response => this.logResponse(endpoint, params, response)),
        catchError(error => {
          this.logError(error)
          return throwError(() => error)
        })
      )
    )
  }

  override async get(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.get(endpoint, params))
  }

  override async getPrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.getPrivate(endpoint, params))
  }

  override async post(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.post(endpoint, params))
  }

  override async postPrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.postPrivate(endpoint, params))
  }

  override async deletePrivate(endpoint: string, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.deletePrivate(endpoint, params))
  }

}
